import { RefObject, useEffect } from "react";

const useFocusTrap = (isOpen: boolean, containerRef: RefObject<HTMLElement>) => {
    useEffect(() => {
        const container = containerRef.current;
        if (isOpen === false || container === null) {
            return;
        }

        const focusableSelectors = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ];

        const getFocusable = () =>
            Array.from(
                container.querySelectorAll<HTMLElement>(
                    focusableSelectors.join(',')
                )
            ).filter(el => el.offsetParent !== null);

        const trap = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;

            const focusables = getFocusable();
            if (focusables.length === 0) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];

            const active = document.activeElement as HTMLElement;
            const isInside = container.contains(active);

            if (isInside === false) {
                e.preventDefault();
                first.focus();
                return;
            }

            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
                return;
            }

            if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
                return;
            }
        };

        const initial = getFocusable()[0];
        initial?.focus();

        container.addEventListener("keydown", trap);
        return () => {
            container.removeEventListener("keydown", trap);
        };
    }, [isOpen]);
};

export default useFocusTrap;
